import { ReactNode, useEffect } from 'react';
import type UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import type WebProPurchaseLocation from '@alltrails/amplitude/enums/WebProPurchaseLocation';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import type SubscriptionsNewResponse from '@alltrails/core/types/SubscriptionsNewResponse';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import useExposureEvent from '@alltrails/experiments/hooks/useExposureEvent';
import type CountryCode from '@alltrails/types/CountryCode';
import type Coupon from '@alltrails/types/Coupon';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import type { Plan } from '@alltrails/types/Plan';
import type PaymentTokenPayload from '../../types/PaymentTokenPayload';
import StripeCheckoutBase from './StripeCheckoutBase';

type Props = {
  ariaLabelledBy?: string;
  // When provided, replaces the default "Billed today"/subheading rows with custom content
  customBillingInfo?: ReactNode;
  billedHeading?: ReactNode;
  billedSubheading?: ReactNode;
  billedSubheadingClassName?: string;
  billedSubheadingRight?: ReactNode;
  billedToday: ReactNode;
  billingCountry: CountryCode;
  buttonText: ReactNode;
  canUpgradeExistingSubscription?: boolean;
  className?: string;
  coupon?: Coupon;
  couponError?: string;
  currencyCode: CurrencyCode;
  finePrint?: ReactNode;
  heading: ReactNode;
  headingClassName?: string;
  isReiCheckout?: boolean;
  isComplianceCheckboxEnabled?: boolean;
  isMultiyearConversion?: boolean;
  isUpgrade?: boolean;
  onBillingCountryChange: (countryCode: CountryCode, response: any) => void;
  onPaymentToken: (payload: PaymentTokenPayload) => void | Promise<void>;
  // Called instead of onPaymentToken when a gift card covers the total, so no payment method is collected
  onSubmitGiftCardRedemption: (payload: { recaptcha: string }) => void;
  plan: Plan;
  promoText?: ReactNode;
  setCoupon?: (coupon: Coupon) => void;
  setPlanToAnnual?: () => void;
  subheading?: ReactNode;
  subscription?: SubscriptionsNewResponse['subscription'];
  webProPurchaseLocation: WebProPurchaseLocation;
  whenExpired?: string;
  purchaseTier: PurchaseTier;
} & ({ isCheckoutModal: true; features?: never; trigger: UpsellTrigger } | { isCheckoutModal?: never; features: ReactNode; trigger?: never });

// All checkout traffic now uses Stripe Elements. The `web-growth-rm-link-checkout` experiment only
// controls which Stripe Link surfaces are enabled.
function CheckoutBaseProvider(props: Props) {
  const experiments = useExperiments();
  const linkCheckoutVariant = experiments['web-growth-rm-link-checkout']?.value;
  const isLinkExpressCheckoutEnabled = linkCheckoutVariant === 'link_express' || linkCheckoutVariant === 'link_both';
  const isLinkPaymentElementEnabled = linkCheckoutVariant === 'link_payment_element' || linkCheckoutVariant === 'link_both';
  const sendLinkCheckoutExposure = useExposureEvent('web-growth-rm-link-checkout');

  useEffect(() => {
    sendLinkCheckoutExposure();
  }, [sendLinkCheckoutExposure]);

  // Upgrades and multiyear conversions are migrated to Stripe Elements; Apple/Google Pay are hidden
  // for those flows inside StripeCheckoutBase due to wallet UX issues (GROW-17814).
  return (
    <StripeCheckoutBase
      ariaLabelledBy={props.ariaLabelledBy}
      customBillingInfo={props.customBillingInfo}
      billedHeading={props.billedHeading}
      billedSubheading={props.billedSubheading}
      billedSubheadingClassName={props.billedSubheadingClassName}
      billedSubheadingRight={props.billedSubheadingRight}
      billedToday={props.billedToday}
      billingCountry={props.billingCountry}
      buttonText={props.buttonText}
      canUpgradeExistingSubscription={props.canUpgradeExistingSubscription ?? false}
      className={props.className}
      coupon={props.coupon}
      couponError={props.couponError}
      currencyCode={props.currencyCode}
      finePrint={props.finePrint}
      heading={props.heading}
      headingClassName={props.headingClassName}
      isComplianceCheckboxEnabled={props.isComplianceCheckboxEnabled}
      isReiCheckout={props.isReiCheckout}
      isLinkExpressCheckoutEnabled={isLinkExpressCheckoutEnabled}
      isLinkPaymentElementEnabled={isLinkPaymentElementEnabled}
      isMultiyearConversion={props.isMultiyearConversion}
      isUpgrade={props.isUpgrade}
      onBillingCountryChange={props.onBillingCountryChange}
      onSubmitGiftCardRedemption={props.onSubmitGiftCardRedemption}
      onPaymentToken={props.onPaymentToken}
      plan={props.plan}
      promoText={props.promoText}
      purchaseTier={props.purchaseTier}
      setCoupon={props.setCoupon}
      setPlanToAnnual={props.setPlanToAnnual}
      subheading={props.subheading}
      subscription={props.subscription}
      webProPurchaseLocation={props.webProPurchaseLocation}
      whenExpired={props.whenExpired}
      {...(props.isCheckoutModal
        ? { isCheckoutModal: true as const, trigger: props.trigger }
        : { isCheckoutModal: undefined, features: props.features })}
    />
  );
}

export default CheckoutBaseProvider;
